/**
 * Authentication Context for Rewards Admin
 * 
 * Manages Google Sign-In via Firebase Identity Platform and provides
 * authentication state and user information throughout the application.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import {
  User,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  IdTokenResult,
} from 'firebase/auth';
import { auth, isAllowedDomain } from '@/app/config/firebase';
import { setTokenProvider } from '@/app/services/apiClient';
import { auditService } from '@/app/services/auditService';

// User profile with role and permissions from custom claims
export interface UserProfile {
  email: string;
  displayName: string | null;
  photoURL: string | null;
  uid: string;
  role: string | null;
  permissions: string[];
  emailVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  // Extract user profile from Firebase user and token claims
  const extractUserProfile = useCallback(async (firebaseUser: User): Promise<UserProfile | null> => {
    if (!firebaseUser.email) return null;

    try {
      // Get ID token result to access custom claims
      const tokenResult: IdTokenResult = await firebaseUser.getIdTokenResult();
      const claims = tokenResult.claims;

      return {
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        uid: firebaseUser.uid,
        role: (claims.role as string) || null,
        permissions: (claims.permissions as string[]) || [],
        emailVerified: firebaseUser.emailVerified,
      };
    } catch (err) {
      console.error('Error extracting user profile:', err);
      return null;
    }
  }, []);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: User | null) => {
      setIsLoading(true);
      setError(null);

      if (firebaseUser) {
        // Check if email domain is allowed
        if (firebaseUser.email && !isAllowedDomain(firebaseUser.email)) {
          // Sign out user with invalid domain
          await firebaseSignOut(auth);
          setUser(null);
          setUserProfile(null);
          setError('Access restricted to authorized email domains (@sequel.ae, @lore.co, @lorehealthcare.com).');
          setIsLoading(false);
          return;
        }

        setUser(firebaseUser);
        try {
          const profile = await extractUserProfile(firebaseUser);
          setUserProfile(profile);
          
          // Log login event if this was a fresh sign-in
          if (justSignedIn.current) {
            justSignedIn.current = false;
            auditService.logLogin('google').catch((err) => {
              console.error('Failed to log login audit event:', err);
            });
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          setError('Failed to load user profile.');
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [extractUserProfile]);

  // Track if we just signed in (to log after profile is extracted)
  const justSignedIn = useRef(false);

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      // Force account selection even if already signed in
      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await signInWithPopup(auth, provider);

      // Check domain before proceeding
      if (result.user.email && !isAllowedDomain(result.user.email)) {
        await firebaseSignOut(auth);
        setError('Access restricted to authorized email domains (@sequel.ae, @lore.co, @lorehealthcare.com).');
        setIsLoading(false);
        return;
      }

      // Mark that we just signed in - audit log will be sent after profile is loaded
      justSignedIn.current = true;
      // Auth state listener will handle the rest
    } catch (err) {
      console.error('Sign in error:', err);
      if (err instanceof Error) {
        if (err.message.includes('popup-closed-by-user')) {
          // User closed the popup, don't show error
          setIsLoading(false);
          return;
        }
        setError(err.message);
      } else {
        setError('Failed to sign in. Please try again.');
      }
      setIsLoading(false);
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    setError(null);
    try {
      // Log logout before signing out (while we still have auth context)
      await auditService.logLogout().catch((err) => {
        console.error('Failed to log logout audit event:', err);
      });
      
      await firebaseSignOut(auth);
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      console.error('Sign out error:', err);
      setError('Failed to sign out. Please try again.');
    }
  }, []);

  // Get ID token for API requests
  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      // Force refresh to get latest custom claims
      return await user.getIdToken(true);
    } catch (err) {
      console.error('Error getting ID token:', err);
      
      // Log session expired when token refresh fails
      auditService.logSessionExpired().catch((auditErr) => {
        console.error('Failed to log session expired audit event:', auditErr);
      });
      
      return null;
    }
  }, [user]);

  // Register token provider with apiClient when user changes
  useEffect(() => {
    if (user) {
      setTokenProvider(getIdToken);
    } else {
      const nullProvider = () => Promise.resolve(null);
      setTokenProvider(nullProvider);
    }
  }, [user, getIdToken]);

  const value: AuthContextType = {
    user,
    userProfile,
    isLoading,
    isAuthenticated: !!user,
    error,
    signInWithGoogle,
    signOut,
    getIdToken,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

